import { useTranslation } from '@govard/i18n-core/react';
import { CheckCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Popconfirm, Spin, Typography } from 'antd';
import type { ReactElement } from 'react';

import { useTelegramLinkMutations, useTelegramLinkStatus } from '../hooks/useTelegramLinking.js';

const { Text } = Typography;
const BOT_USERNAME = 'aigcrmbot';

export function TelegramLinkPanel(): ReactElement {
  const { t } = useTranslation();
  const query = useTelegramLinkStatus();
  const mutations = useTelegramLinkMutations();

  if (query.isLoading) {
    return (
      <Card size="small" title={t('crm.settings.telegram.link_title')}>
        <Spin aria-label={t('crm.settings.telegram.link_loading')} />
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card size="small" title={t('crm.settings.telegram.link_title')}>
        <Alert type="warning" showIcon title={t('crm.settings.telegram.link_load_error')} />
      </Card>
    );
  }

  const status = query.data?.status ?? 'not_linked';
  const connect = async () => {
    const issued = await mutations.issue.mutateAsync();
    const url = `https://t.me/${BOT_USERNAME}?start=${encodeURIComponent(issued.token)}`;
    mutations.issue.reset();
    window.location.assign(url);
  };

  return (
    <Card size="small" title={t('crm.settings.telegram.link_title')}>
      {status === 'active' ? (
        <div className="flex w-full flex-col gap-4">
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            title={t('crm.settings.telegram.link_active')}
            description={t('crm.settings.telegram.link_active_description')}
          />
          <Popconfirm
            title={t('crm.settings.telegram.disconnect_title')}
            description={t('crm.settings.telegram.disconnect_description')}
            okText={t('crm.settings.telegram.disconnect_confirm')}
            cancelText={t('crm.common.cancel')}
            onConfirm={() => mutations.disconnect.mutateAsync()}
          >
            <Button danger loading={mutations.disconnect.isPending}>
              {t('crm.settings.telegram.disconnect')}
            </Button>
          </Popconfirm>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start gap-4">
          <Text type="secondary">
            {status === 'suspended'
              ? t('crm.settings.telegram.link_suspended')
              : t('crm.settings.telegram.link_description')}
          </Text>
          <Button
            type="primary"
            icon={<LinkOutlined />}
            loading={mutations.issue.isPending}
            onClick={() => void connect()}
          >
            {status === 'suspended'
              ? t('crm.settings.telegram.reconnect')
              : t('crm.settings.telegram.connect')}
          </Button>
        </div>
      )}
      {mutations.issue.isError || mutations.disconnect.isError ? (
        <Alert
          className="mt-3"
          type="error"
          showIcon
          title={t('crm.settings.telegram.link_error')}
        />
      ) : null}
    </Card>
  );
}
